export const demandPatterns = {
  CONSTANT: {
    id: 'constant',
    name: 'Constant',
    description: 'Fixed interval between orders',
    icon: '📊',
  },
  RANDOM: {
    id: 'random',
    name: 'Random',
    description: 'Variable arrival times (Normal distribution)',
    icon: '🎲',
  },
  SEASONAL: {
    id: 'seasonal',
    name: 'Seasonal',
    description: 'Fluctuating demand with peaks and valleys',
    icon: '📈',
  },
  TRENDING: {
    id: 'trending',
    name: 'Trending',
    description: 'Gradually changing demand over time',
    icon: '📉',
  },
  BURST: {
    id: 'burst',
    name: 'Burst',
    description: 'Sudden spikes at regular intervals',
    icon: '💥',
  },
};

export const distributionTypes = {
  FIXED: {
    id: 'fixed',
    name: 'Fixed',
    description: 'Same quantity every time',
    icon: '📦',
  },
  NORMAL: {
    id: 'normal',
    name: 'Normal',
    description: 'Bell curve around mean',
    icon: '🔔',
  },
  UNIFORM: {
    id: 'uniform',
    name: 'Uniform',
    description: 'Equal probability between min-max',
    icon: '📏',
  },
  POISSON: {
    id: 'poisson',
    name: 'Poisson',
    description: 'Event-based arrivals',
    icon: '⚡',
  },
  EXPONENTIAL: {
    id: 'exponential',
    name: 'Exponential',
    description: 'Time between rare events',
    icon: '⏱️',
  },
};

export const getPatternPreview = (pattern, params) => {
  const points = [];
  const numPoints = 30;
  
  switch (pattern) {
    case 'constant':
      for (let i = 0; i < numPoints; i++) {
        points.push({ x: i, y: params.interval || 1 });
      }
      break;
      
    case 'random':
      const avgInterval = params.avgInterval || 1;
      const variance = params.variance || 0.2;
      for (let i = 0; i < numPoints; i++) {
        const random = avgInterval + (Math.random() - 0.5) * 2 * variance * avgInterval;
        points.push({ x: i, y: Math.max(0.1, random) });
      }
      break;
      
    case 'seasonal':
      const base = params.baseInterval || 1;
      const amplitude = params.amplitude || 0.5;
      const period = params.period || 10;
      for (let i = 0; i < numPoints; i++) {
        const seasonal = base + amplitude * Math.sin((2 * Math.PI * i) / period);
        points.push({ x: i, y: Math.max(0.1, seasonal) });
      }
      break;
      
    case 'trending':
      const start = params.startInterval || 1;
      const end = params.endInterval || 2;
      for (let i = 0; i < numPoints; i++) {
        const trend = start + (end - start) * (i / (numPoints - 1));
        points.push({ x: i, y: trend });
      }
      break;
      
    case 'burst':
      const normal = params.normalInterval || 1;
      const burst = params.burstInterval || 0.2;
      const freq = params.burstFrequency || 5;
      for (let i = 0; i < numPoints; i++) {
        const isBurst = i % freq === 0;
        points.push({ x: i, y: isBurst ? burst : normal });
      }
      break;
      
    default:
      for (let i = 0; i < numPoints; i++) {
        points.push({ x: i, y: 1 });
      }
  }
  
  return points;
};

export const getDistributionPreview = (distType, params, baseQty) => {
  const points = [];
  const samples = 100;
  let values = [];
  
  // Generate samples
  switch (distType) {
    case 'fixed':
      values = Array(samples).fill(baseQty);
      break;
      
    case 'normal':
      const mean = params.mean || baseQty;
      const stdDev = params.std_dev || 50;
      for (let i = 0; i < samples; i++) {
        // Box-Muller transform for normal distribution
        const u1 = Math.random();
        const u2 = Math.random();
        const z0 = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
        values.push(Math.max(0, Math.round(mean + z0 * stdDev)));
      }
      break;
      
    case 'uniform':
      const min = params.min || (baseQty - 100);
      const max = params.max || (baseQty + 100);
      for (let i = 0; i < samples; i++) {
        values.push(Math.round(min + Math.random() * (max - min)));
      }
      break;
      
    case 'poisson':
      const lambda = params.lambda || baseQty;
      for (let i = 0; i < samples; i++) {
        let L = Math.exp(-lambda);
        let k = 0;
        let p = 1;
        do {
          k++;
          p *= Math.random();
        } while (p > L);
        values.push(k - 1);
      }
      break;
      
    case 'exponential':
      const scale = params.scale || baseQty;
      for (let i = 0; i < samples; i++) {
        values.push(Math.round(-Math.log(Math.random()) * scale));
      }
      break;
      
    default:
      values = Array(samples).fill(baseQty);
  }
  
  // Create histogram
  const histogram = {};
  values.forEach(val => {
    histogram[val] = (histogram[val] || 0) + 1;
  });
  
  // Convert to chart data
  Object.keys(histogram).sort((a, b) => a - b).forEach(key => {
    points.push({ x: parseInt(key), y: histogram[key] });
  });
  
  return points;
};